import { RolesTenantsMappings } from '@components/Authorization';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import { stringRequiredValidationScheme } from '@utils/validation';
import { addDays, getUnixTime } from 'date-fns';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React from 'react';
import {
	AccessKeyExpiration,
	IGenerateAccessKey,
	IGeneratedAccessKey
} from 'types/accessKeys';
import { ToastSeverity } from 'types/toast';
import { object } from 'yup';
import AccessKeyExpirationField from './AccessKeyExpirationField';
import PermittedProjects from './PermittedProjects';

interface IGenerateAccessKeyDialogProps {
	onCancel: () => void;
	onSubmit: (generatedKey: string) => void;
	generateKey: (key: IGenerateAccessKey) => Promise<IGeneratedAccessKey>;
	defaultExpiration: AccessKeyExpiration;
	showTenantMapping?: boolean;
	showPermittedProjects?: boolean;
	translationPrefix: string;
}

const AccessKeyGenerateDialog: React.FC<IGenerateAccessKeyDialogProps> = ({
	onSubmit,
	onCancel,
	generateKey,
	defaultExpiration,
	translationPrefix,
	showTenantMapping,
	showPermittedProjects
}) => {
	const { addToast } = useToast();

	const formik = useFormik<IGenerateAccessKey>({
		initialValues: {
			name: '',
			mappings: [],
			allProjects: true,
			permittedProjects: [],
			expireTime: defaultExpiration
				? getUnixTime(addDays(new Date(), defaultExpiration))
				: defaultExpiration
		},
		validationSchema: object({
			name: stringRequiredValidationScheme()
		}),
		onSubmit: async (values) => {
			try {
				const { cleartext } = await generateKey(values);

				addToast({
					severity: ToastSeverity.Success,
					content: intl.tr(
						`${translationPrefix}.action.add.dialog.toast.success`
					)
				});

				onSubmit(cleartext);
			} catch (error) {
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr(
						`${translationPrefix}.action.add.dialog.toast.error`
					),
					error
				});
			}
		}
	});

	return (
		<Dialog open maxWidth="md" data-testid="generate-access-key-dialog">
			<DialogTitle>
				{intl.tr(`${translationPrefix}.action.add.dialog.title`)}
			</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextFieldValidation
								id="name"
								formik={formik}
								fullWidth
								autoFocus
								required
								label={intl.tr('fields.accessKeys.column.name')}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<AccessKeyExpirationField
								formik={formik}
								id="expireTime"
								defaultExpiration={defaultExpiration}
							/>
						</Grid>
						{showTenantMapping && (
							<Grid item xs={12} data-testid="access-key-roles-mapping">
								<Typography pl={1} py={1}>
									{intl.tr('dialog.authorization.header')}
								</Typography>
								<RolesTenantsMappings formik={formik} id="mappings" />
							</Grid>
						)}
						{showPermittedProjects && (
							<PermittedProjects formik={formik} enabled />
						)}
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onCancel}
					data-testid="access-key-creation-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="access-key-creation-dialog-approve-button"
				>
					{intl.tr('accessKeys.button.generate')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

AccessKeyGenerateDialog.defaultProps = {
	showTenantMapping: false,
	showPermittedProjects: false
};

export type { IGenerateAccessKeyDialogProps };
export default AccessKeyGenerateDialog;
